import { gql, type TypedDocumentNode } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button/index.tsx';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Gift } from '@sorare/core/src/atoms/icons/Gift.tsx';
import { Users } from '@sorare/core/src/atoms/icons/Users.tsx';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons/index.tsx';
import { iconAddressCard } from '@sorare/core/src/atoms/icons/system/iconAddressCard.ts';
import { iconCheck } from '@sorare/core/src/atoms/icons/system/iconCheck.ts';
import { iconX } from '@sorare/core/src/atoms/icons/system/iconX.ts';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex/index.tsx';
import {
  BrandM,
  BrandS,
  BrandXS,
} from '@sorare/core/src/atoms/typography/index.tsx';
import { TextureBackground } from '@sorare/core/src/atoms/ui/TextureBackground/index.tsx';
import { Trapezoid } from '@sorare/core/src/atoms/ui/Trapezoid/index.tsx';
import { Header } from '@sorare/core/src/components/dialog/Header/index.tsx';
import { Dialog } from '@sorare/core/src/components/dialog/index.tsx';
import { SeriesBalance } from '@sorare/core/src/components/navigation/AppNavigation/UserCurrencies/SeriesBalance/index.tsx';
import { SocialShare } from '@sorare/core/src/components/user/SocialShare/index.tsx';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize.ts';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery.ts';
import {
  socialShareEventContext,
  socialShareEventName,
} from '@sorare/core/src/lib/events.ts';
import { isTypeFn } from '@sorare/core/src/lib/gql/index.ts';
import { useCommonGameSet } from '@sorare/core/src/set';

import { TaskClaimedDialogs } from '#src/components/missions/TaskClaimedDialogs/index.tsx';
import { useTaskClaimedDialogs } from '#src/components/missions/TaskClaimedDialogs/useTaskClaimedDialogs.tsx';
import { NeonRewardConfigs } from '#src/components/rewards/NeonRewards/NeonRewardConfigs/index.tsx';
import { useInviteShareMessage } from '#src/hooks/useInviteShareMessage.ts';

import { ReferralMission } from './ReferralMission/index.tsx';
import { ReferralMissionDetails } from './ReferralMissionDetails/index.tsx';
import { ReferralMissionDialog } from './ReferralMissionDialog/index.tsx';
import type {
  ConnectTaskDialogQuery,
  ConnectTaskDialogQueryVariables,
} from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 1.5 })`
  position: relative;
  min-height: 100%;
  isolation: isolate;
`;
const StyledTextureBackground = styled(TextureBackground)`
  position: absolute;
  inset: 0;
  z-index: -1;
`;
const HeaderWrapper = styled.div`
  width: 100%;
  position: sticky;
  top: 0;
`;
const FooterWrapper = styled(Horizontal).attrs({ center: true })`
  width: 100%;
  position: sticky;
  bottom: var(--double-unit);
  padding-top: var(--double-unit);
  margin-top: auto;
`;
const MobileContent = styled(Vertical).attrs({ gap: 4, center: true })`
  padding: var(--intermediate-unit) var(--double-unit);
`;

const ContentWrapper = styled(Vertical).attrs({ gap: 0 })`
  position: relative;
  z-index: 1;
  overflow: hidden;
  max-height: 90vh;
  width: min(calc(80 * var(--vw)), 820px);
`;
const HeaderRow = styled(SBHorizontal)`
  padding: var(--double-unit);
  align-items: center;
  flex-shrink: 0;
  border-bottom: 1px solid var(--c-nd-150);
`;
const HeaderTitle = styled(Horizontal).attrs({ gap: 0.5, center: true })`
  flex: 1;
  justify-content: center;
`;
const TwoPanelLayout = styled.div`
  display: grid;
  grid-template-columns: 380px 1fr;
  overflow: hidden;
`;
const LeftPanel = styled(Vertical).attrs({ gap: 0 })`
  overflow-y: auto;
  padding: var(--double-unit);
  border-right: 1px solid var(--c-nd-150);
`;
const RightPanel = styled(Vertical).attrs({ gap: 0 })`
  overflow: hidden;
  flex: 1;
  min-width: 0;
`;
const RightPanelContent = styled(Vertical).attrs({ gap: 3 })`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
`;
const RightPanelBackground = styled(TextureBackground)`
  position: absolute;
  inset: 0;
  z-index: -1;
`;
const LeftPanelContent = styled(Vertical).attrs({ gap: 4 })`
  padding: var(--double-unit) 0;
`;

const ConnectTitle = styled(Trapezoid)`
  background: var(--c-set-surface-primary);
`;
const SummaryLabel = styled(BrandXS)`
  color: var(--c-nd-600);
  text-transform: uppercase;
`;
const SummaryValue = styled(BrandS)`
  text-shadow: 0px 0px 8px rgba(255, 255, 255, 0.58);
`;
const SectionTitle = styled(Horizontal).attrs({ gap: 0.5 })`
  width: 100%;
  align-items: center;
`;
const StyledSpan = styled.span`
  color: var(--c-nd-600);
`;

const CONNECT_TASK_DIALOG_QUERY = gql`
  query ConnectTaskDialogQuery($sport: Sport!) {
    cardSet {
      id
      maximumInviteTasksAsReferrer(sport: $sport)
    }
    currentUser {
      slug
      referrerCode
      inviteTasksAsReferrer(sport: $sport) {
        id
        ...ReferralMission_inviteTaskAsReferrer
        ...ReferralMissionDialog_inviteTaskAsReferrer
        ... on InviteTaskAsReferrer {
          id
          steps {
            id
            aasmState
            rewardConfigs {
              id
              ...NeonRewardConfigs_rewardConfig
            }
          }
        }
      }
    }
  }
  ${NeonRewardConfigs.fragments.rewardConfig}
  ${ReferralMission.fragments.inviteTaskAsReferrer}
  ${ReferralMissionDialog.fragments.inviteTaskAsReferrer}
` as TypedDocumentNode<ConnectTaskDialogQuery, ConnectTaskDialogQueryVariables>;

type Props = {
  open: boolean;
  onClose: () => void;
};
export const ConnectTaskDialog = ({ open, onClose }: Props) => {
  const { sport } = useSportContext();
  const { visual, slug } = useCommonGameSet();
  const { SetIcon } = visual.brand;
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const { up: isLaptop } = useScreenSize('laptop');
  const {
    onClaim,
    pickerDialogTask,
    setPickerDialogTask,
    revealPackCardsDialogReward,
    setRevealPackCardsDialogReward,
    rewardDialogTask,
    setRewardDialogTask,
  } = useTaskClaimedDialogs();

  const { data, refetch } = useQuery(CONNECT_TASK_DIALOG_QUERY, {
    variables: { sport },
    skip: !open,
  });

  const { currentUser, cardSet } = data || {};
  const { maximumInviteTasksAsReferrer } = cardSet || {};
  const { referrerCode } = currentUser || {};
  const inviteShareMessage = useInviteShareMessage(referrerCode);

  const inviteTasks = (currentUser?.inviteTasksAsReferrer || []).filter(
    isTypeFn('InviteTaskAsReferrer')
  );
  const claimNowInviteTasks = inviteTasks.filter(task =>
    task.steps?.some(
      step => step.aasmState === ManagerProgressionTaskState.COMPLETED
    )
  );
  const ongoingInviteTasks = inviteTasks.filter(task =>
    task.steps?.every(
      step =>
        step.aasmState === ManagerProgressionTaskState.READY ||
        step.aasmState === ManagerProgressionTaskState.CLAIMED
    )
  );
  const claimedInviteTasks = inviteTasks.filter(
    task => task.aasmState === ManagerProgressionTaskState.CLAIMED
  );

  const friendsRemaining =
    (maximumInviteTasksAsReferrer || 10) - inviteTasks.length;

  if (isLaptop && !selectedTaskId && inviteTasks.length > 0 && open) {
    setSelectedTaskId(inviteTasks[0].id);
  }

  const selectedTask = inviteTasks.find(t => t.id === selectedTaskId);

  const inviteButton = (
    <SocialShare
      message={inviteShareMessage}
      renderButton={({ onClick }) => (
        <Button size="large" color="primary" onClick={onClick}>
          <FormattedMessage
            defaultMessage="Invite friends <span>({friendsRemaining, number} left)</span>"
            values={{
              friendsRemaining,
              span: chunks => <StyledSpan>{chunks}</StyledSpan>,
            }}
          />
        </Button>
      )}
      trackingEventName={socialShareEventName.SHARE_INVITE_FRIENDS_SERIES}
      trackingEventContext={socialShareEventContext.SERIES_INVITE_FRIENDS}
    />
  );

  const rewardConfigs = inviteTasks.flatMap(task =>
    task.steps
      .filter(step => step.aasmState === ManagerProgressionTaskState.CLAIMED)
      .flatMap(step => step.rewardConfigs)
  );

  const listContent = (
    <>
      <Vertical gap={2} center>
        <SummaryLabel>
          <FormattedMessage defaultMessage="Your progress" />
        </SummaryLabel>
        <Horizontal gap={2}>
          <Horizontal gap={0.5}>
            <Users width={20} height={20} color="var(--c-blue-300)" />
            <SummaryValue>{inviteTasks.length}</SummaryValue>
          </Horizontal>
          <NeonRewardConfigs
            size={16}
            iconSize={2}
            displayLabels={false}
            compact
            rewardConfigs={rewardConfigs}
          />
        </Horizontal>
      </Vertical>

      <Vertical gap={4}>
        {claimNowInviteTasks.length > 0 && (
          <Vertical gap={2}>
            <SectionTitle>
              <Gift size={2} color="var(--c-set-surface-primary)" />
              <BrandS bold>
                <FormattedMessage defaultMessage="Claim now" />
              </BrandS>
            </SectionTitle>
            {claimNowInviteTasks.map(task => (
              <ReferralMission
                key={task.id}
                task={task}
                onClick={() => setSelectedTaskId(task.id)}
                onClaim={onClaim}
                onClaimed={refetch}
              />
            ))}
          </Vertical>
        )}

        {ongoingInviteTasks.length > 0 && (
          <Vertical gap={2}>
            <SectionTitle>
              <FontAwesomeIcon
                icon={iconAddressCard}
                color="var(--c-set-surface-primary)"
              />
              <BrandS bold>
                <FormattedMessage defaultMessage="Invited friends" />
              </BrandS>
            </SectionTitle>
            {ongoingInviteTasks.map(task => (
              <ReferralMission
                key={task.id}
                task={task}
                onClick={() => setSelectedTaskId(task.id)}
                onClaim={onClaim}
              />
            ))}
          </Vertical>
        )}

        {claimedInviteTasks.length > 0 && (
          <Vertical gap={2}>
            <SectionTitle>
              <FontAwesomeIcon
                icon={iconCheck}
                color="var(--c-set-surface-primary)"
              />
              <BrandS bold>
                <FormattedMessage defaultMessage="Completed" />
              </BrandS>
            </SectionTitle>
            {claimedInviteTasks.map(task => (
              <ReferralMission
                key={task.id}
                task={task}
                onClick={() => setSelectedTaskId(task.id)}
                onClaim={onClaim}
              />
            ))}
          </Vertical>
        )}
      </Vertical>
    </>
  );

  if (isLaptop) {
    return (
      <Dialog open={open} onClose={onClose} hideHeader>
        <ContentWrapper>
          <HeaderRow>
            <SeriesBalance cardSetSlugs={[slug]} />

            <HeaderTitle>
              <ConnectTitle>
                <BrandM color="var(--c-black)">
                  <Horizontal gap={0.5} center>
                    <SetIcon size={3} />
                    <FormattedMessage defaultMessage="Connect" />
                  </Horizontal>
                </BrandM>
              </ConnectTitle>
            </HeaderTitle>
            <IconButton
              size="small"
              onClick={onClose}
              icon={iconX}
              color="quaternary"
            />
          </HeaderRow>

          <TwoPanelLayout>
            <LeftPanel>
              <LeftPanelContent>
                {listContent}
                {inviteButton}
              </LeftPanelContent>
            </LeftPanel>
            <RightPanel>
              {!!selectedTask && (
                <RightPanelContent>
                  <RightPanelBackground
                    background="wave1"
                    mode="dark"
                    color="var(--c-set-surface-tertiary)"
                  />
                  <ReferralMissionDetails task={selectedTask} />
                </RightPanelContent>
              )}
            </RightPanel>
          </TwoPanelLayout>
        </ContentWrapper>
        <TaskClaimedDialogs
          pickerDialogTask={pickerDialogTask}
          onPickerDialogClose={() => {
            setPickerDialogTask(null);
          }}
          revealPackCardsDialogReward={revealPackCardsDialogReward}
          onRevealPackCardsDialogClose={() => {
            setRevealPackCardsDialogReward(null);
          }}
          rewardDialogTask={rewardDialogTask}
          onRewardDialogClose={() => {
            setRewardDialogTask(null);
          }}
        />
      </Dialog>
    );
  }

  return (
    <Dialog open={open} fullWidth maxWidth="xs" hideHeader>
      <Root>
        <StyledTextureBackground
          background="wave1"
          mode="dark"
          color="var(--c-set-surface-primary)"
        />
        <HeaderWrapper>
          <Header onBack={onClose} />
        </HeaderWrapper>
        <MobileContent>
          <ConnectTitle>
            <BrandM color="var(--c-black)">
              <Horizontal gap={0.5} center>
                <SetIcon size={3} />
                <FormattedMessage defaultMessage="Connect" />
              </Horizontal>
            </BrandM>
          </ConnectTitle>

          {listContent}
        </MobileContent>

        <FooterWrapper>{inviteButton}</FooterWrapper>
      </Root>
      {!!selectedTask && (
        <ReferralMissionDialog
          open={!!selectedTask}
          onClose={() => setSelectedTaskId(null)}
          task={selectedTask}
        />
      )}
      <TaskClaimedDialogs
        pickerDialogTask={pickerDialogTask}
        onPickerDialogClose={() => {
          setPickerDialogTask(null);
        }}
        revealPackCardsDialogReward={revealPackCardsDialogReward}
        onRevealPackCardsDialogClose={() => {
          setRevealPackCardsDialogReward(null);
        }}
        rewardDialogTask={rewardDialogTask}
        onRewardDialogClose={() => {
          setRewardDialogTask(null);
        }}
      />
    </Dialog>
  );
};
